// noinspection ExceptionCaughtLocallyJS

import {
  GoogleOneTapSignIn,
  GoogleSignin,
  isErrorWithCode,
  isNoSavedCredentialFoundResponse,
  isSuccessResponse
} from '@react-native-google-signin/google-signin'
import { type Session } from '@supabase/supabase-js'
import { IconAlertCircle } from '@tabler/icons-react-native'
import * as AppleAuthentication from 'expo-apple-authentication'
import { router } from 'expo-router'
import { useCallback, useEffect, useState, useTransition, type FC, type ReactNode } from 'react'
import { AppState, InteractionManager, Platform } from 'react-native'

import { HStack, Icon, SplashScreen, Toast, ToastTitle, useToast } from '@/components'
import { AuthContext, type AuthSignInCredentials, type AuthSignUpCredentials } from '@/contexts'
import { initializeRelayEnvironment, supabase } from '@/lib'

export interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const toast = useToast()
  const [isSigningOut, startSignOut] = useTransition()
  const [isSigningIn, startSignIn] = useTransition()
  const [isSigningUp, startSignUp] = useTransition()
  const [loaded, setLoaded] = useState(false)
  const [session, setSession] = useState<Session | null>(null)

  const refreshSession = useCallback(async () => {
    const { data } = await supabase.auth.getSession()

    setSession(data?.session)
  }, [])
  const showToastError = useCallback(
    (message: string) =>
      toast.show({
        placement: 'bottom',
        render: () => (
          <Toast
            action='warning'
            variant='solid'
          >
            <HStack className='gap-2'>
              <Icon
                className='color-black'
                as={IconAlertCircle}
                size='xl'
              />
              <ToastTitle className='text-wrap pr-8'>{message}</ToastTitle>
            </HStack>
          </Toast>
        )
      }),
    [toast]
  )
  const signUpWithCredentials = useCallback(
    ({ email, password, firstName, lastName }: AuthSignUpCredentials) =>
      startSignUp(async () => {
        try {
          const supabaseResponse = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                firstName,
                lastName
              }
            }
          })

          if (__DEV__) {
            console.debug('[supabase auth] sign up response', supabaseResponse)
          }

          if (supabaseResponse.error) {
            throw supabaseResponse.error
          }

          if (!supabaseResponse.data.user) {
            throw new Error('Invalid or missing user.')
          }

          if (
            // their email is not yet verified and a confirmation was sent
            supabaseResponse.data.user.confirmation_sent_at &&
            !supabaseResponse.data.user.user_metadata.email_verified
          ) {
            return router.dismissTo('/account-unverified')
          }

          router.replace('/')
        } catch (err: any) {
          console.error('[supabase auth] sign up error', err)
          showToastError('message' in err ? err.message : String(err))
        }
      }),
    [showToastError]
  )
  const signInWithCredentials = useCallback(
    ({ email, password }: AuthSignInCredentials) =>
      startSignIn(async () => {
        try {
          let signInWithPasswordResponse = await supabase.auth.signInWithPassword({ email, password })

          if (__DEV__) {
            console.debug('[supabase auth] sign in response', signInWithPasswordResponse)
          }

          if (signInWithPasswordResponse.error) {
            // one time only, run the migrate legacy password function and retry
            const migrateLegacyPasswordResponse = await supabase.functions.invoke<string>('migrate_legacy_password', {
              body: { email, password },
              method: 'POST'
            })

            if (!migrateLegacyPasswordResponse.error) {
              signInWithPasswordResponse = await supabase.auth.signInWithPassword({ email, password })
            }
          }

          if (signInWithPasswordResponse.error) {
            throw signInWithPasswordResponse.error
          }

          if (!signInWithPasswordResponse.data.user) {
            throw new Error('Invalid or missing user.')
          }

          router.replace('/')
        } catch (err: any) {
          console.error('[supabase auth] sign in error', err)

          switch (err.code) {
            case 'user_banned':
              showToastError(
                'This account has been permanently banned for violating our Terms of Service. Please note that if you are subscribed to any premium services through the App Store or Google Play, you will need to cancel your subscription with the appropriate provider.'
              )
              break
            case 'email_not_confirmed':
              // @todo (ryan) - should I limit how often we resend this?
              await supabase.auth.resend({
                type: 'signup',
                email: email
              })
              router.replace('/account-unverified')
              break
            default:
              showToastError('message' in err ? err.message : String(err))
          }
        }
      }),
    [showToastError]
  )
  const signInWithApple = useCallback(
    () =>
      startSignIn(async () => {
        try {
          const appleResponse = await AppleAuthentication.signInAsync({
            requestedScopes: [
              AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
              AppleAuthentication.AppleAuthenticationScope.EMAIL
            ]
          })

          if (__DEV__) {
            console.debug('[apple auth] sign in response', appleResponse)
          }

          if (!appleResponse?.identityToken) {
            throw new Error('Missing Apple authentication data.')
          }

          const supabaseResponse = await supabase.auth.signInWithIdToken({
            provider: 'apple',
            token: appleResponse.identityToken
          })

          if (supabaseResponse.error) {
            throw supabaseResponse.error
          }

          if (!supabaseResponse.data.user) {
            throw new Error('Invalid or missing user.')
          }

          if (appleResponse.email && !supabaseResponse.data.user.email) {
            await supabase.auth.updateUser({
              email: appleResponse.email
            })
          }

          if (appleResponse.fullName?.familyName && appleResponse.fullName?.givenName) {
            // first, update the supabase auth user's metadata to match how other providers give their data (e.g. Google)
            await supabase.auth.updateUser({
              data: {
                ...supabaseResponse.data.user.user_metadata,
                givenName: appleResponse.fullName.givenName,
                familyName: appleResponse.fullName.familyName
              }
            })
          }

          router.replace('/')
        } catch (err: any) {
          console.error('[apple auth] sign in error', err)

          if ('code' in err && err.code === 'user_banned') {
            showToastError('This account has been permanently banned for violating our Terms of Service.')
          } else if ('code' in err && err.code !== 'ERR_REQUEST_CANCELED') {
            showToastError('message' in err ? err.message : String(err))
          }
        }
      }),
    [showToastError]
  )
  const signInWithGoogle = useCallback(
    () =>
      startSignIn(async () => {
        GoogleOneTapSignIn.configure({
          logLevel: __DEV__ ? 'debug' : 'warn',
          webClientId: process.env.EXPO_PUBLIC_GOOGLE_AUTH_WEB_CLIENT_ID!
        })

        if (__DEV__) {
          console.debug('[google auth] configured')
        }

        try {
          if (Platform.OS === 'web') {
            // noinspection JSUnusedGlobalSymbols
            GoogleOneTapSignIn.signIn(
              {
                itp_support: true,
                log_level: __DEV__ ? 'debug' : 'warn',
                use_fedcm_for_prompt: true
              },
              {
                onResponse: (response) => {
                  if (__DEV__) {
                    console.debug('[google auth] sign in response', response)
                  }

                  if (response.type === 'success') {
                    supabase.auth
                      .signInWithIdToken({
                        provider: 'google',
                        token: response.data.idToken
                      })
                      .then(async ({ data, error }) => {
                        if (error) {
                          throw error
                        }

                        if (!data?.user?.id) {
                          throw new Error('Invalid or missing user.')
                        }

                        router.replace('/')
                      })
                  }
                },
                onError: (error) => {
                  console.error('[google auth] sign in error', error)
                  throw error
                }
              }
            )
          } else {
            await GoogleOneTapSignIn.checkPlayServices()

            // check first if the user has previously signed into this app, and if so, sign them in.
            // on iOS this is a silent keychain restore, and a stale credential makes it reject with
            // `invalid_grant` rather than report "no saved credential" — only the "expired or revoked"
            // wording is translated — which would otherwise strand that device forever
            let googleResponse = await GoogleOneTapSignIn.signIn().catch((err) => {
              console.warn('[google auth] silent sign in failed, falling back to explicit', err)

              return null
            })

            if (__DEV__) {
              console.debug('[google auth] sign in response', googleResponse)
            }

            if (!googleResponse || isNoSavedCredentialFoundResponse(googleResponse)) {
              // the user wasn't previously signed into this app, or the stored credential is no longer
              // usable — drop it so the next launch starts clean instead of failing the same way. this
              // is best effort: on Android it clears the credential state and can itself reject, which
              // would put us right back to stranding the user
              await GoogleSignin.signOut().catch((err) => console.warn('[google auth] sign out failed', err))

              googleResponse = await GoogleOneTapSignIn.createAccount()

              if (isNoSavedCredentialFoundResponse(googleResponse)) {
                // no Google user account was present on the device yet (unlikely but possible)
                googleResponse = await GoogleOneTapSignIn.presentExplicitSignIn()
              }
            }

            if (isSuccessResponse(googleResponse)) {
              const supabaseResponse = await supabase.auth.signInWithIdToken({
                provider: 'google',
                token: googleResponse.data.idToken
              })

              if (supabaseResponse.error) {
                throw supabaseResponse.error
              }

              if (!supabaseResponse.data.user) {
                throw new Error('Invalid or missing user.')
              }

              router.replace('/')
            }
          }
        } catch (err: any) {
          console.error('[google auth] sign in error', err)

          if (isErrorWithCode(err) && err.code === 'user_banned') {
            showToastError('This account has been permanently banned for violating our Terms of Service.')
          } else {
            showToastError(isErrorWithCode(err) ? err.message : String(err))
          }
        }
      }),
    [showToastError]
  )
  const signOut = useCallback(
    (cb?: () => void) =>
      startSignOut(async () => {
        await supabase.auth.signOut()
        cb?.()
        router.replace('/sign-in')
        // the (user) screens stay mounted through the fade, and swapping underneath them leaves their preloaded
        // queries pointing at the old environment while context holds the new one, which relay warns about
        InteractionManager.runAfterInteractions(initializeRelayEnvironment)
      }),
    []
  )

  useEffect(() => {
    const {
      data: { subscription: authStateListener }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (__DEV__) {
        console.debug('[supabase] auth event:', event, session)
      }

      setSession(session)
      setLoaded(true)
    })
    const appStateListener = AppState.addEventListener('change', async (status) => {
      if (status === 'active') {
        if (__DEV__) {
          console.debug('[supabase] starting token auto-refresh')
        }

        await supabase.auth.startAutoRefresh()
      } else {
        if (__DEV__) {
          console.debug('[supabase] stopping token auto-refresh')
        }

        await supabase.auth.stopAutoRefresh()
      }
    })

    return () => {
      authStateListener.unsubscribe()
      appStateListener.remove()
    }
  }, [])

  if (!loaded) {
    return <SplashScreen />
  }

  return (
    <AuthContext.Provider
      value={{
        isSigningIn,
        isSigningOut,
        isSigningUp,
        refreshSession,
        session,
        signInWithApple,
        signInWithCredentials,
        signInWithGoogle,
        signOut,
        signUpWithCredentials
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
